/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX } from 'react';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
} from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  $isEditorIsNestedEditor,
  IS_APPLE,
  mergeRegister,
} from '@lexical/utils';
import {
  $addUpdateTag,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  CommandPayloadType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  HISTORIC_TAG,
  INDENT_CONTENT_COMMAND,
  LexicalCommand,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  SKIP_DOM_SELECTION_TAG,
  TextFormatType,
  UNDO_COMMAND,
} from 'lexical';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  blockTypeToBlockName,
  useToolbarState,
} from '../../context/ToolbarContext';
import useModal from '../../hooks/useModal';
import DropDown, { DropDownItem } from '../../ui/DropDown';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { formatBulletList, formatCheckList, formatNumberedList } from './utils';
import DropdownColorPicker from '@/components/LexicalEditor/ui/DropdownColorPicker';
import { InsertImageDialog } from '@/components/LexicalEditor/plugins/ImagesPlugin';
import { INSERT_EMBED_COMMAND } from '@lexical/react/LexicalAutoEmbedPlugin';
import { InsertTableDialog } from '@/components/LexicalEditor/plugins/TablePlugin';
import { INSERT_COLLAPSIBLE_COMMAND } from '@/components/LexicalEditor/plugins/CollapsiblePlugin';

const FONT_SIZE_OPTIONS: [string, string][] = [
  ['8px', '8px'],
  ['10px', '10px'],
  ['12px', '12px'],
  ['14px', '14px'],
  ['16px', '16px'],
  ['18px', '18px'],
  ['20px', '20px'],
  ['22px', '22px'],
  ['24px', '24px'],
  ['26px', '26px'],
  ['28px', '28px'],
  ['32px', '32px'],
  ['36px', '36px'],
  ['48px', '48px'],
  ['72px', '72px'],
];

function dropDownActiveClass(active: boolean) {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

function Divider(): JSX.Element {
  return <div className="divider" />;
}

// 단축키 표기는 번역 대상이 아니므로 라벨과 분리해 조립
function withShortcut(label: string, macKeys: string, winKeys: string): string {
  return `${label} (${IS_APPLE ? macKeys : winKeys})`;
}

/**
 * 회의록 본문 에디터 전용 '재정리 요청' 버튼 옵션 (PORTAL-12850).
 * prop 을 전달한 호출부(회의록)에서만 버튼이 노출된다 — 게시판 등 다른
 * BoardEditor 사용처에는 영향 없음.
 */
export type ReorganizeButtonProps = {
  onClick: () => void;
  /** 재정리 진행 중 — 버튼 비활성 + 스피너 */
  isLoading?: boolean;
  /** 정리할 입력(글/녹음)이 없어 실행 불가 */
  disabled?: boolean;
};

function ReorganizeButton({
  onClick,
  isLoading,
  disabled,
}: ReorganizeButtonProps): JSX.Element {
  const { t } = useTranslation();
  const label = t('재정리 요청');

  return (
    <StyledReorganizeButton
      disabled={disabled || isLoading}
      onClick={onClick}
      type="button"
      aria-label={label}
      title={label}
    >
      {isLoading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        // 연필 아이콘 — 색은 currentColor 로 버튼 상태(활성/비활성) 색을 따른다
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.86133 15.4877L15.083 15.4877" strokeLinecap="round" />
          <path
            d="M15.178 7.05914L7.74315 14.494L4.55798 14.9006L4.97392 11.7248L12.4088 4.2899L15.178 7.05914Z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label}
    </StyledReorganizeButton>
  );
}

// 회의록 AI 계열 강조색(#C77AC4)/비활성색(#D1D8E2)은 ActionButtonBase 와 동일 토큰
const StyledReorganizeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  color: #c77ac4;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: var(--dk-purple-soft, #f8eff8);
  }

  &:disabled {
    color: var(--dk-text-disabled, #d1d8e2);
    cursor: auto;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

function FontDropDown({
  editor,
  value,
  style,
  disabled = false,
  onClick,
}: {
  editor: LexicalEditor;
  value: string;
  style: string;
  disabled?: boolean;
  onClick?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const handleClick = useCallback(
    (option: string) => {
      editor.update(() => {
        // $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, {
            [style]: option,
          });
        }
      });
    },
    [editor, style],
  );

  return (
    <DropDown
      disabled={disabled}
      buttonClassName={'toolbar-item ' + style}
      buttonLabel={value}
      buttonAriaLabel={t('글자 크기 옵션')}
      onClick={onClick}
    >
      {FONT_SIZE_OPTIONS.map(([option, text]) => (
        <DropDownItem
          className={`item ${dropDownActiveClass(
            value === option,
          )} fontsize-item`}
          onClick={() => handleClick(option)}
          key={option}
        >
          <span className="text">{text}</span>
        </DropDownItem>
      ))}
    </DropDown>
  );
}

function $findTopLevelElement(node: LexicalNode) {
  let topLevelElement =
    node.getKey() === 'root'
      ? node
      : $findMatchingParent(node, (e) => {
          const parent = e.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        });

  if (topLevelElement === null) {
    topLevelElement = node.getTopLevelElementOrThrow();
  }
  return topLevelElement;
}

export default function ToolbarPlugin({
  editor,
  activeEditor,
  setActiveEditor,
  setIsLinkEditMode,
  isSimple,
  toolbarClick,
  reorganizeButtonProps,
}: {
  editor: LexicalEditor;
  activeEditor: LexicalEditor;
  setActiveEditor: Dispatch<LexicalEditor>;
  setIsLinkEditMode: Dispatch<boolean>;
  isSimple?: boolean;
  toolbarClick: () => void;
  /** 회의록 전용 '재정리 요청' 버튼 — 전달 시에만 툴바 맨 왼쪽에 노출 */
  reorganizeButtonProps?: ReorganizeButtonProps;
}): JSX.Element {
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(
    null,
  );
  const [modal, showModal] = useModal();
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const { t } = useTranslation();
  const { toolbarState, updateToolbarState, defaultFontSize } =
    useToolbarState();

  const dispatchToolbarCommand = <T extends LexicalCommand<unknown>>(
    command: T,
    payload: CommandPayloadType<T> | undefined = undefined,
    skipRefocus = false,
  ) => {
    activeEditor.update(() => {
      if (skipRefocus) {
        $addUpdateTag(SKIP_DOM_SELECTION_TAG);
      }

      // Re-assert on Type so that payload can have a default param
      activeEditor.dispatchCommand(command, payload as CommandPayloadType<T>);
    });
  };

  const dispatchFormatTextCommand = (
    payload: TextFormatType,
    skipRefocus = false,
  ) => dispatchToolbarCommand(FORMAT_TEXT_COMMAND, payload, skipRefocus);

  const $handleHeadingNode = useCallback(
    (selectedElement: LexicalNode) => {
      const type = $isHeadingNode(selectedElement)
        ? selectedElement.getTag()
        : selectedElement.getType();

      if (type in blockTypeToBlockName) {
        updateToolbarState(
          'blockType',
          type as keyof typeof blockTypeToBlockName,
        );
      }
    },
    [updateToolbarState],
  );

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
        const rootElement = activeEditor.getRootElement();
        updateToolbarState(
          'isImageCaption',
          !!rootElement?.parentElement?.classList.contains(
            'image-caption-container',
          ),
        );
      } else {
        updateToolbarState('isImageCaption', false);
      }

      const anchorNode = selection.anchor.getNode();
      const element = $findTopLevelElement(anchorNode);
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      updateToolbarState('isRTL', $isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const isLink = $isLinkNode(parent) || $isLinkNode(node);
      updateToolbarState('isLink', isLink);

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        updateToolbarState('rootType', 'table');
      } else {
        updateToolbarState('rootType', 'root');
      }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();

          updateToolbarState('blockType', type);
        } else {
          $handleHeadingNode(element);
        }
      }

      // Handle buttons
      updateToolbarState(
        'fontColor',
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      updateToolbarState(
        'bgColor',
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      updateToolbarState(
        'elementFormat',
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
          ? node.getFormatType()
          : parent?.getFormatType() || 'left',
      );
    }
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      // Update text format
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
      updateToolbarState(
        'isStrikethrough',
        selection.hasFormat('strikethrough'),
      );
      updateToolbarState('isSubscript', selection.hasFormat('subscript'));
      updateToolbarState('isSuperscript', selection.hasFormat('superscript'));
      updateToolbarState('isHighlight', selection.hasFormat('highlight'));
      updateToolbarState('isCode', selection.hasFormat('code'));
      updateToolbarState(
        'fontSize',
        $getSelectionStyleValueForProperty(
          selection,
          'font-size',
          `${defaultFontSize}px`,
        ),
      );
      updateToolbarState('isLowercase', selection.hasFormat('lowercase'));
      updateToolbarState('isUppercase', selection.hasFormat('uppercase'));
      updateToolbarState('isCapitalize', selection.hasFormat('capitalize'));
    }
    if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      for (const selectedNode of nodes) {
        const parentList = $getNearestNodeOfType<ListNode>(
          selectedNode,
          ListNode,
        );
        if (parentList) {
          const type = parentList.getListType();
          updateToolbarState('blockType', type);
        } else {
          const selectedElement = $findTopLevelElement(selectedNode);
          $handleHeadingNode(selectedElement);
          // Update elementFormat for node selection (e.g., images)
          if ($isElementNode(selectedElement)) {
            updateToolbarState(
              'elementFormat',
              selectedElement.getFormatType(),
            );
          }
        }
      }
    }
  }, [
    activeEditor,
    editor,
    updateToolbarState,
    $handleHeadingNode,
    defaultFontSize,
  ]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar, setActiveEditor]);

  useEffect(() => {
    activeEditor.getEditorState().read(
      () => {
        $updateToolbar();
      },
      { editor: activeEditor },
    );
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor: activeEditor },
        );
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState('canUndo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState('canRedo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [$updateToolbar, activeEditor, editor, updateToolbarState]);

  const applyStyleText = useCallback(
    (
      styles: Record<string, string>,
      skipHistoryStack?: boolean,
      skipRefocus = false,
    ) => {
      activeEditor.update(
        () => {
          if (skipRefocus) {
            $addUpdateTag(SKIP_DOM_SELECTION_TAG);
          }
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: HISTORIC_TAG } : {},
      );
    },
    [activeEditor],
  );

  const onFontColorSelect = useCallback(
    (color: string) => {
      applyStyleText({ color }); // skipHistoryStack, skipRefocus 기본값 사용
    },
    [applyStyleText],
  );

  const onBgColorSelect = useCallback(
    (color: string) => {
      applyStyleText({ 'background-color': color });
    },
    [applyStyleText],
  );

  const insertLink = useCallback(() => {
    if (!toolbarState.isLink) {
      setIsLinkEditMode(true);
      activeEditor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        sanitizeUrl('https://'),
      );
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, setIsLinkEditMode, toolbarState.isLink]);

  // 넘친 버튼은 스크롤바 없이 밀어야 보임 — 마우스 휠도 옆으로 밀고, 끝에 닿으면 페이지가 스크롤됨
  const toolbarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const delta =
        e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaY;
      const next = Math.min(max, Math.max(0, el.scrollLeft + delta));
      if (next === el.scrollLeft) return;
      e.preventDefault();
      el.scrollLeft = next;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const canViewerSeeInsertDropdown = !toolbarState.isImageCaption;
  const canViewerSeeInsertCodeButton = !toolbarState.isImageCaption;

  const undoLabel = withShortcut(t('되돌리기'), '⌘Z', 'Ctrl+Z');
  const redoLabel = withShortcut(t('다시 실행'), '⌘Y', 'Ctrl+Y');
  const boldLabel = withShortcut(t('굵게'), '⌘B', 'Ctrl+B');
  const italicLabel = withShortcut(t('기울임'), '⌘I', 'Ctrl+I');
  const underlineLabel = withShortcut(t('밑줄'), '⌘U', 'Ctrl+U');

  return (
    <div className="toolbar" ref={toolbarRef}>
      {/* 재정리 요청(회의록 전용) — 서식 버튼들과 구분선으로 분리해 맨 왼쪽 배치 */}
      {reorganizeButtonProps && (
        <>
          <ReorganizeButton {...reorganizeButtonProps} />
          <Divider />
        </>
      )}
      <>
        <button
          disabled={!toolbarState.canUndo || !isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
            toolbarClick();
          }}
          title={undoLabel}
          type="button"
          className="toolbar-item spaced"
          aria-label={undoLabel}
        >
          <i className="format undo" />
        </button>
        <button
          disabled={!toolbarState.canUndo || !isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(REDO_COMMAND, undefined);
            toolbarClick();
          }}
          title={redoLabel}
          type="button"
          className="toolbar-item"
          aria-label={redoLabel}
        >
          <i className="format redo" />
        </button>

        <Divider />
      </>

      <>
        {/* 폰트(패밀리) 변경 기능 제거(PM 결정) — 폰트 크기만 유지 */}
        <FontDropDown
          disabled={!isEditable}
          style={'font-size'}
          value={toolbarState.fontSize}
          editor={editor}
          onClick={toolbarClick}
        />
        <Divider />
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' + (toolbarState.isBold ? 'active' : '')
          }
          title={boldLabel}
          type="button"
          aria-label={boldLabel}
        >
          <i className="format bold" />
        </button>
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' + (toolbarState.isItalic ? 'active' : '')
          }
          title={italicLabel}
          type="button"
          aria-label={italicLabel}
        >
          <i className="format italic" />
        </button>
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' + (toolbarState.isUnderline ? 'active' : '')
          }
          title={underlineLabel}
          type="button"
          aria-label={underlineLabel}
        >
          <i className="format underline" />
        </button>

        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' +
            (toolbarState.isStrikethrough ? 'active' : '')
          }
          title={t('취소선')}
          type="button"
          aria-label={t('취소선')}
        >
          <i className="format strikethrough" />
        </button>

        <Divider />

        {!isSimple && (
          <>
            {' '}
            <DropdownColorPicker
              disabled={!isEditable}
              buttonClassName="toolbar-item color-picker"
              buttonAriaLabel={t('글자 색 설정')}
              buttonIconClassName="icon font-color"
              color={toolbarState.fontColor}
              onChange={onFontColorSelect}
              title={t('글자 색')}
              arrowHidden={true}
              onClick={toolbarClick}
            />
            <button
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'highlight');
                toolbarClick();
              }}
              className={
                'toolbar-item spaced ' +
                (toolbarState.isHighlight ? 'active' : '')
              }
              aria-label={t('형광펜')}
              title={t('형광펜')}
              type="button"
            >
              <i className="format Highlight" />
            </button>
            <DropdownColorPicker
              disabled={!isEditable}
              buttonClassName="toolbar-item color-picker"
              buttonAriaLabel={t('배경 색 설정')}
              buttonIconClassName="format bg-color"
              color={toolbarState.bgColor}
              onChange={onBgColorSelect}
              title={t('배경 색')}
              arrowHidden={true}
              onClick={toolbarClick}
            />
            <Divider />
            <button
              disabled={!isEditable}
              onClick={() => {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('왼쪽 정렬')}
              title={t('왼쪽 정렬')}
              type="button"
            >
              <i className="format left-align" />
            </button>
            <button
              disabled={!isEditable}
              onClick={() => {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('가운데 정렬')}
              title={t('가운데 정렬')}
              type="button"
            >
              <i className="format center-align" />
            </button>
            <button
              disabled={!isEditable}
              onClick={() => {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('오른쪽 정렬')}
              title={t('오른쪽 정렬')}
              type="button"
            >
              <i className="format right-align" />
            </button>
            <Divider />
          </>
        )}

        <button
          disabled={!isEditable}
          onClick={() => {
            formatBulletList(editor, toolbarState.blockType);
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' +
            (toolbarState.blockType === 'bullet' ? 'active' : '')
          }
          aria-label={t('글머리 기호 목록')}
          title={t('글머리 기호 목록')}
          type="button"
        >
          <i className="format bullet-list" />
        </button>

        <button
          disabled={!isEditable}
          onClick={() => {
            formatNumberedList(editor, toolbarState.blockType);
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' +
            (toolbarState.blockType === 'number' ? 'active' : '')
          }
          aria-label={t('번호 목록')}
          title={t('번호 목록')}
          type="button"
        >
          <i className="format numbered-list" />
        </button>

        <button
          disabled={!isEditable}
          onClick={() => {
            formatCheckList(editor, toolbarState.blockType);
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' +
            (toolbarState.blockType === 'check' ? 'active' : '')
          }
          aria-label={t('체크 목록')}
          title={t('체크 목록')}
          type="button"
        >
          <i className="format check-list" />
        </button>

        <Divider />

        {!isSimple && (
          <>
            <button
              disabled={!isEditable}
              onClick={() => {
                editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('내어쓰기')}
              title={t('내어쓰기')}
              type="button"
            >
              <i className="format outdent" />
            </button>
            <button
              disabled={!isEditable}
              onClick={() => {
                editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
                toolbarClick();
              }}
              className={'toolbar-item spaced '}
              aria-label={t('들여쓰기')}
              title={t('들여쓰기')}
              type="button"
            >
              <i className="format indent" />
            </button>

            <Divider />
          </>
        )}

        <button
          disabled={!isEditable}
          onClick={() => {
            insertLink();
            toolbarClick();
          }}
          className={
            'toolbar-item spaced ' + (toolbarState.isLink ? 'active' : '')
          }
          aria-label={t('링크 삽입')}
          title={t('링크 삽입')}
          type="button"
        >
          <i className="format link" />
        </button>

        {!isSimple && (
          <>
            <button
              disabled={!isEditable}
              onClick={() => {
                showModal(
                  t('이미지 삽입'),
                  (onClose) => (
                    <InsertImageDialog
                      activeEditor={activeEditor}
                      onClose={onClose}
                    />
                  ),
                  false,
                );
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('이미지 삽입')}
              title={t('이미지 삽입')}
              type="button"
            >
              <i className="format image" />
            </button>

            <button
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(
                  INSERT_EMBED_COMMAND,
                  'youtube-video',
                );
                toolbarClick();
              }}
              className={'toolbar-item spaced'}
              aria-label={t('유튜브 삽입')}
              title={t('유튜브 삽입')}
              type="button"
            >
              <i className="format youtube" />
            </button>

            <button
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(
                  INSERT_EMBED_COMMAND,
                  'embed-iframe',
                );
                toolbarClick();
              }}
              className={
                'toolbar-item spaced ' + (toolbarState.isLink ? 'active' : '')
              }
              aria-label={t('iframe 코드 삽입')}
              title={t('iframe 코드 삽입')}
              type="button"
            >
              <i className="format code" />
            </button>
          </>
        )}

        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined);
            toolbarClick();
          }}
          className={'toolbar-item spaced'}
          aria-label={t('토글 삽입')}
          title={t('토글 삽입')}
          type="button"
        >
          <i className="format toggle" />
        </button>

        <button
          disabled={!isEditable}
          onClick={() => {
            showModal(
              t('표 삽입'),
              (onClose) => (
                <InsertTableDialog
                  activeEditor={activeEditor}
                  onClose={onClose}
                />
              ),
              false,
            );
            toolbarClick();
          }}
          className={'toolbar-item spaced'}
          aria-label={t('표 삽입')}
          title={t('표 삽입')}
          type="button"
        >
          <i className="format table" />
        </button>
      </>

      {/*<button*/}
      {/*  disabled={!isEditable}*/}
      {/*  onClick={clearFormatting}*/}
      {/*  className={'toolbar-item spaced'}*/}
      {/*  aria-label="Clear all text formatting"*/}
      {/*  title="Clear text formatting"*/}
      {/*  type="button"*/}
      {/*>*/}
      {/*  <i className="format clear" />*/}
      {/*</button>*/}

      {modal}
    </div>
  );
}
